/**
 * RelationshipGraph.jsx — iter-arc1
 *
 * SVG-based circular force layout for the character relationship map.
 * No D3 required: characters are placed on a circle, edges connect them,
 * relationship type determines edge colour and style.
 *
 * Props:
 *   rows       — array from story_bible.relationship_map (char_a, char_b, type, description)
 *   characters — character cards array (id, name)
 */
import React, { useMemo, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

// ── Edge colour by relationship type ──────────────────────────────────────────
const TYPE_CFG = {
  romantic:    { stroke: '#f43f5e', label: 'Romantic',    dash: '0'     },
  love_interest:{ stroke: '#f43f5e', label: 'Love',       dash: '0'     },
  friendship:  { stroke: '#22c55e', label: 'Friendship',  dash: '0'     },
  alliance:    { stroke: '#3b82f6', label: 'Alliance',    dash: '0'     },
  mentor:      { stroke: '#a855f7', label: 'Mentor',      dash: '5,4'   },
  family:      { stroke: '#f97316', label: 'Family',      dash: '0'     },
  rivalry:     { stroke: '#ef4444', label: 'Rivalry',     dash: '6,3'   },
  antagonist:  { stroke: '#ef4444', label: 'Antagonist',  dash: '3,3'   },
  betrayal:    { stroke: '#dc2626', label: 'Betrayal',    dash: '8,3,2,3'},
  complex:     { stroke: '#94a3b8', label: 'Complex',     dash: '4,2'   },
  foil:        { stroke: '#8b5cf6', label: 'Foil',        dash: '5,3'   },
};
const DEFAULT_CFG = { stroke: '#94a3b8', label: '', dash: '4,2' };

// ── Minimal helpers ───────────────────────────────────────────────────────────
function circlePt(cx, cy, r, angleDeg) {
  const a = (angleDeg - 90) * (Math.PI / 180);
  return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
}

function midPt(x1, y1, x2, y2) {
  return { x: (x1 + x2) / 2, y: (y1 + y2) / 2 };
}

// ── Legend ────────────────────────────────────────────────────────────────────
function Legend({ types }) {
  if (!types.length) return null;
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 px-1 pb-1">
      {types.map((t) => {
        const cfg = TYPE_CFG[t] || DEFAULT_CFG;
        return (
          <div key={t} className="flex items-center gap-1.5">
            <svg width="22" height="8">
              <line
                x1="2" y1="4" x2="20" y2="4"
                stroke={cfg.stroke}
                strokeWidth="2"
                strokeDasharray={cfg.dash}
              />
            </svg>
            <span className="text-[11px] text-muted-foreground">{cfg.label || t}</span>
          </div>
        );
      })}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function RelationshipGraph({ rows, characters }) {
  const [hover, setHover] = useState(null); // { edgeIdx, x, y }
  const svgRef = useRef(null);

  const W = 480, H = 480, CX = 240, CY = 240;
  // Radius of the character node ring — scales with node count
  const nodeCount = characters.length;
  const ringR = Math.min(170, Math.max(90, 50 + nodeCount * 15));
  const nodeR = Math.max(20, Math.min(32, 200 / (nodeCount + 2)));

  // Build an index: name → position on circle
  const nodeMap = useMemo(() => {
    const map = {};
    characters.forEach((c, i) => {
      const angle = (360 / characters.length) * i;
      const pt = { ...circlePt(CX, CY, ringR, angle), name: c.name, angle, r: nodeR };
      map[c.name] = pt;              // lookup by name (AI rows)
      if (c.id) map[c.id] = pt;     // lookup by id (manually-added rows)
    });
    return map;
  }, [characters, ringR, nodeR]);

  // Unique relationship types in this map (for legend)
  const usedTypes = useMemo(() => {
    const s = new Set((rows || []).map((r) => ((r.type || r.relationship_type) || 'complex').toLowerCase()));
    return [...s];
  }, [rows]);

  if (!rows || rows.length === 0 || characters.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
        <p className="text-[13px] text-muted-foreground">
          No relationships to visualise yet. Add rows in the table view.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2" data-testid="relationship-graph-container">
      <Legend types={usedTypes} />
      <div className="relative mx-auto w-full max-w-[480px]">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${W} ${H}`}
          className="w-full"
          style={{ maxHeight: 440 }}
          onMouseLeave={() => setHover(null)}
        >
          {/* ── Edges ── */}
          {(rows || []).map((row, i) => {
            // iter-arc1: Support both AI-generated rows (char_a/char_b) and
            // manually-added rows (character_a_name/character_b_name + character_a_id/character_b_id).
            const aName = row.char_a || row.character_a_name || row.character_a_id || '';
            const bName = row.char_b || row.character_b_name || row.character_b_id || '';
            const edgeType = row.type || row.relationship_type || 'complex';
            const a = nodeMap[aName];
            const b = nodeMap[bName];
            if (!a || !b) return null;
            const cfg = TYPE_CFG[(edgeType || '').toLowerCase()] || DEFAULT_CFG;
            const mid = midPt(a.x, a.y, b.x, b.y);
            // Slight curve: control point shifted toward centre
            const ctrl = { x: mid.x + (CX - mid.x) * 0.35, y: mid.y + (CY - mid.y) * 0.35 };
            const isHovered = hover?.edgeIdx === i;
            return (
              <g key={i}>
                <path
                  d={`M ${a.x} ${a.y} Q ${ctrl.x} ${ctrl.y} ${b.x} ${b.y}`}
                  fill="none"
                  stroke={cfg.stroke}
                  strokeWidth={isHovered ? 3 : 1.5}
                  strokeDasharray={cfg.dash}
                  strokeOpacity={isHovered ? 1 : 0.65}
                  style={{ transition: 'stroke-width 150ms, stroke-opacity 150ms', cursor: 'pointer' }}
                  onMouseEnter={(e) => {
                    const svgRect = svgRef.current?.getBoundingClientRect();
                    if (!svgRect) return;
                    setHover({
                      edgeIdx: i,
                      row,
                      x: e.clientX - svgRect.left,
                      y: e.clientY - svgRect.top,
                    });
                  }}
                  onMouseLeave={() => setHover(null)}
                  data-testid={`rel-graph-edge-${i}`}
                />
                {/* Midpoint label (short type tag) */}
                {!isHovered && (
                  <text
                    x={ctrl.x}
                    y={ctrl.y - 4}
                    textAnchor="middle"
                    fontSize="8"
                    fill={cfg.stroke}
                    fillOpacity={0.6}
                    pointerEvents="none"
                  >
                    {(cfg.label || row.type || '').toLowerCase()}
                  </text>
                )}
              </g>
            );
          })}

          {/* ── Nodes ── */}
          {Object.values(nodeMap).map((n) => (
            <g key={n.name} data-testid={`rel-graph-node-${n.name}`}>
              <circle
                cx={n.x} cy={n.y} r={n.r}
                fill="hsl(var(--primary)/0.12)"
                stroke="hsl(var(--primary)/0.5)"
                strokeWidth={1.5}
              />
              {/* Name — split long names across two lines */}
              {(() => {
                const words = n.name.split(' ');
                const line1 = words.slice(0, Math.ceil(words.length / 2)).join(' ');
                const line2 = words.slice(Math.ceil(words.length / 2)).join(' ');
                const hasTwo = line2.length > 0;
                return (
                  <>
                    <text
                      x={n.x} y={n.y + (hasTwo ? -5 : 4)}
                      textAnchor="middle"
                      fontSize={Math.max(8, Math.min(11, n.r * 0.52))}
                      fontWeight="600"
                      fill="hsl(var(--foreground))"
                      pointerEvents="none"
                    >{line1}</text>
                    {hasTwo && (
                      <text
                        x={n.x} y={n.y + 8}
                        textAnchor="middle"
                        fontSize={Math.max(8, Math.min(11, n.r * 0.52))}
                        fontWeight="600"
                        fill="hsl(var(--foreground))"
                        pointerEvents="none"
                      >{line2}</text>
                    )}
                  </>
                );
              })()}
            </g>
          ))}
        </svg>

        {/* ── Hover tooltip ── */}
        {hover && hover.row && (
          <div
            className="pointer-events-none absolute z-20 max-w-[200px] rounded-lg border border-border bg-popover px-3 py-2 shadow-lg text-[11.5px] leading-snug"
            style={{ left: hover.x + 12, top: hover.y - 10, transform: 'translateY(-50%)' }}
          >
            <div className="font-semibold text-foreground">
              {hover.row.char_a || hover.row.character_a_name} ↔ {hover.row.char_b || hover.row.character_b_name}
            </div>
            <div className="mt-0.5 capitalize text-muted-foreground">{hover.row.type || hover.row.relationship_type}</div>
            {(hover.row.description || hover.row.notes) && (
              <div className="mt-1 text-muted-foreground">{hover.row.description || hover.row.notes}</div>
            )}
          </div>
        )}
      </div>
      <p className="text-center text-[11px] text-muted-foreground">
        Hover an edge to see the relationship description
      </p>
    </div>
  );
}
